import React, {FunctionComponent, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {PageContext} from "../../context/PageContext";
import {OptionContext} from "../../context/OptionContext";
import {ItemsContext} from "../../context/ItemsContext";
import {InvoiceContext} from "../../context/InvoiceContext";
import {GeneralProvider} from "../../context/GeneralContext";
import {FullPagePreloader} from "../../components/FullPagePreloader/FullPagePreloader";
import {MiddlePagePreloader} from "../../components/MiddlePagePreloader/MiddlePagePreloader";
import {VariantProposalLayout} from "../../components/VariantProposal/VariantProposalLayout";
import {VariantProposalAcceptanceModal} from "../../components/VariantProposal/VariantProposalAcceptanceModal";
import {VariantProposalAcceptedModal} from "../../components/VariantProposal/VariantProposalAcceptedModal";
import {InvoiceOption, ItemSection} from "../../models/item.model";
import {Invoice} from "../../models/invoice.model";

export const VariantProposalPage: FunctionComponent = () => {
    const pageContext = useContext(PageContext);
    const optionContext = useContext(OptionContext);
    const itemSection: ItemSection = useContext(ItemsContext);
    const invoice: Invoice = useContext(InvoiceContext);
    const [acceptanceOptionId, setAcceptanceOptionId] = useState<number | null>(null);

    const options: InvoiceOption[] = useMemo(() => itemSection.options || [], [itemSection.options]);
    const isAccepted = pageContext.isAcceptedSignaturePage();
    const acceptanceOption = useMemo(() => {
        return options.find((option: InvoiceOption) => option.id === acceptanceOptionId) || null;
    }, [options, acceptanceOptionId]);


    const onSelectCard = useCallback((option: InvoiceOption) => {
        optionContext.selectOption(option);
        setAcceptanceOptionId(option.id);
    }, [optionContext]);

    const onCancelAcceptance = useCallback(() => {
        setAcceptanceOptionId(null);
        optionContext.clearSelection();
    }, [optionContext]);

    useEffect(() => {
        if (acceptanceOptionId === null) {
            return;
        }
        const freshOption = options.find((option: InvoiceOption) => option.id === acceptanceOptionId);
        if (!freshOption) {
            onCancelAcceptance();
        } else if (freshOption !== optionContext.selectedOption) {
            optionContext.selectOption(freshOption);
        }
    }, [options]);

    const highlightedOptionId = isAccepted ? (optionContext.selectedOption?.id ?? null) : acceptanceOptionId;

    return (
        <>
            {pageContext.isActivePage()
                ? <GeneralProvider>
                    <FullPagePreloader/>
                    <MiddlePagePreloader/>
                </GeneralProvider>
                : <>
                    <FullPagePreloader/>
                    <MiddlePagePreloader/>
                </>}

            <VariantProposalLayout title={invoice.title || invoice.invoice_title}
                                   options={options}
                                   highlightedOptionId={highlightedOptionId}
                                   isInteractive={!isAccepted}
                                   onSelectOption={onSelectCard}/>

            {!isAccepted && acceptanceOption &&
                <VariantProposalAcceptanceModal option={acceptanceOption} onClose={onCancelAcceptance}/>}

            {isAccepted && <VariantProposalAcceptedModal option={optionContext.selectedOption}/>}
        </>
    );
};
